import * as Sentry from '@sentry/browser'
import axios from 'axios'
import he from 'he'
import _ from 'lodash'

import config from '@/config.js'
import { isOrgSwitchHandoffActive } from '@/lib/orgSwitchHandoff'
import UserAuthentication from '@/auth'

const isNotificationsRequest = stringResponse => /"read":/.test(stringResponse)

const axiosWithInterceptors = axios.create({
  transformResponse: [
    data => {
      // This implementation is necessary for decoding HTML entities like &lt; $gt; into the character they represent
      try {
        if (_.isObject(data) || !data) return data
        return JSON.parse(he.decode(data ?? ''))
      } catch (err) {
        // Old notifications have some issues that must be solved on the "notifications" micro-service. We have to check if the response is related to notifications and report errors only if they don't so we don't exceed our Sentry error quota. Once https://rever.atlassian.net/browse/BAC-1915 is solved, we can safely remove this "if" statement
        if (!isNotificationsRequest(data)) {
          Sentry.captureException(
            `Transform Response Error: Error parsing data: ${data}`
          )
        }
        return JSON.parse(data)
      }
    },
  ],
})

axiosWithInterceptors.interceptors.request.use(
  axiosConfig => {
    const auth = new UserAuthentication(window.localStorage)

    axiosConfig.baseURL = config.api
    axiosConfig.headers = {
      'Content-Type': 'application/json',
      Authorization: auth.getAccessToken() ?? '',
      ...axiosConfig.headers,
    }

    return axiosConfig
  },
  error => {
    return Promise.reject(error)
  }
)

axiosWithInterceptors.interceptors.response.use(
  response => response,
  error => {
    const requestURL = error?.config?.url ?? ''
    const baseURL = error?.config?.baseURL ?? ''
    const requestPath = requestURL.replace(baseURL, '')

    if (error?.response?.status !== 401) return Promise.reject(error)

    if (isOrgSwitchHandoffActive()) return Promise.reject(error)

    if (
      !requestPath.includes('/api/v1/login') &&
      !window.location.pathname.includes('/access')
    ) {
      window.localStorage.clear()
      window.location.replace('/logout')
    }

    return Promise.reject(error)
  }
)

export const HTTP = axiosWithInterceptors
export default axiosWithInterceptors
